<template>
  <div class="test-drive-calendar" data-app>
    <!-- Calendar Toolbar -->
    <v-sheet class="calendar-toolbar mt-3 mb-3">
      <!-- ACTIONS -->
      <div class="d-flex align-items-center justify-content-between w-100 p-0">
        <div class="d-flex align-items-center" style="column-gap: 1rem">
          <div class="d-flex align-items-center">
            <v-btn-toggle
              v-model="type"
              mandatory
              dense
              rounded
              class="elevation-0"
            >
              <v-btn value="month" small>
                <v-icon left>mdi-calendar-month</v-icon>
                Tháng
              </v-btn>
              <v-btn value="day" small>
                <v-icon left>mdi-calendar-today</v-icon>
                Ngày
              </v-btn>
            </v-btn-toggle>
          </div>
          <div class="d-flex align-items-center">
            <v-btn
              fab
              text
              x-small
              color="grey darken-2 bg-secondary"
              style="background-color: #f5f5f5 !important"
              @click="prev"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-toolbar-title class="mx-1" style="font-size: small">{{
              title
            }}</v-toolbar-title>
            <v-btn
              fab
              text
              x-small
              color="grey darken-2"
              style="background-color: #f5f5f5 !important"
              @click="next"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- LEGENDS -->
        <div class="d-flex align-items-center justify-content-between">
          <v-sheet>
            <div class="d-flex flex-wrap">
              <div
                class="status-legend"
                v-for="(status, index) in statusLegend"
                :key="index"
              >
                <span
                  class="status-dot"
                  :style="{ backgroundColor: status.color }"
                ></span>
                <span
                  :class="index === statusLegend.length - 1 ? 'mr-0' : 'mr-3'"
                  class="status-text"
                  style="font-size: small"
                  >{{ status.text }}</span
                >
              </div>
            </div>
          </v-sheet>
        </div>
      </div>
    </v-sheet>

    <!-- Calendar Content -->
    <v-sheet
      class="calendar-content"
      width="100%"
      :height="type === 'month' ? '1480' : 'auto'"
    >
      <v-calendar
        ref="calendar"
        locale="vi"
        v-model="focus"
        :events="events"
        :event-color="getEventColor"
        :now="today"
        :type="type"
        :event-overlap-threshold="30"
        :weekdays="[1, 2, 3, 4, 5, 6, 0]"
        :event-height="55"
        :event-margin-bottom="5"
        first-time="06:00"
        interval-minutes="30"
        interval-count="35"
        interval-height="40"
        mode="column"
        @click:event="showEvent"
        @click:more="viewDay"
        @click:date="viewDay"
        @change="updateRange"
      >
        <template v-slot:event="{ event }">
          <div
            class="event-content d-flex flex-column p-2"
            :style="{
              color: event.textColor,
              'font-weight': '900',
            }"
          >
            <div class="font-weight-bold text-truncate">
              {{ event.productName }}
            </div>
            <div class="text-caption text-truncate">
              Biển số: {{ event.licensePlate }}
            </div>
            <div class="text-caption text-truncate">
              {{ event.timeRange }}
            </div>
          </div>
        </template>
      </v-calendar>

      <!-- Event Details Popup -->
      <v-menu
        v-model="selectedOpen"
        :close-on-content-click="false"
        :activator="selectedElement"
        offset-y
        max-width="350"
      >
        <v-card color="grey lighten-4">
          <v-toolbar
            :color="selectedEvent.color"
            dark
            class="justify-content-between align-items-center"
          >
            <v-toolbar-title
              v-html="selectedEvent.name"
              class="align-items-center"
              :style="{ color: selectedEvent.textColor }"
            ></v-toolbar-title>

            <v-btn
              icon
              @click="editSelectedEvent"
              v-if="canEdit(selectedEvent.originalItem)"
              :style="{ color: selectedEvent.textColor }"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="selectedOpen = false"
              :style="{ color: selectedEvent.textColor }"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <div class="mb-2">
              <strong>Mã sản phẩm:</strong> {{ selectedEvent.productName }}
            </div>
            <div class="mb-2">
              <strong>Khách hàng:</strong>
              {{ selectedEvent.customerName || '-' }}
            </div>
            <div class="mb-2">
              <strong>Biển số:</strong> {{ selectedEvent.licensePlate }}
            </div>
            <div class="mb-2">
              <strong>Showroom:</strong> {{ selectedEvent.showroomName || '-' }}
            </div>
            <div class="mb-2">
              <strong>Thời gian:</strong> {{ selectedEvent.timeRange }}
            </div>
            <div class="mb-2">
              <strong>Người sử dụng:</strong>
              {{ selectedEvent.employeeName || '-' }}
            </div>
            <div class="mb-2">
              <strong>Trạng thái:</strong>
              <span
                :class="selectedEvent.statusClass"
                class="label font-weight-bold label-lg label-inline"
                >{{ selectedEvent.statusText }}</span
              >
            </div>
          </v-card-text>
        </v-card>
      </v-menu>
    </v-sheet>
  </div>
</template>

<script>
import {
  DATETIME_DB_FORMAT,
  DP_CONFIG,
  YEAR_MONTH_DAY_DATETIME_FORMAT,
} from '../../../../utils/date';
import { TestDriveModel } from '../../posm/models/TestDrive.model';
import moment from 'moment';

export default {
  name: 'TestDriveCalendar',

  props: {
    testDrives: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focus: '',
      type: 'month',
      typeToLabel: {
        day: 'Ngày',
        week: 'Tuần',
        month: 'Tháng',
      },
      today: moment().format('YYYY-MM-DD'),
      start: null,
      end: null,
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      events: [],
      dateMenu: false,
      selectedDate: moment().format('YYYY-MM-DD'),
      statusLegend: [
        { text: 'Đăng ký', color: 'rgb(255, 168, 0)' }, // Light warning - vàng
        { text: 'Xác nhận', color: 'rgb(54, 153, 255)' }, // Light primary - xanh
        { text: 'Hoàn thành', color: 'rgb(27, 197, 189)' }, // Light success - xanh lá cây
      ],

      statusColors: {
        [TestDriveModel.STATUS.REGISTERED]: {
          bgColor: 'rgb(255, 244, 222)',
          textColor: 'rgb(255, 168, 0)',
        }, // Light warning - vàng
        [TestDriveModel.STATUS.CONFIRMED]: {
          bgColor: 'rgb(225, 240, 255)',
          textColor: 'rgb(54, 153, 255)',
        }, // Light primary - xanh
        [TestDriveModel.STATUS.COMPLETED]: {
          bgColor: 'rgb(201, 247, 245)',
          textColor: 'rgb(27, 197, 189)',
        }, // Light success - xanh lá cây
      },

      configs: {
        date: DP_CONFIG.date,
      },
    };
  },
  computed: {
    title() {
      const { start, end } = this;
      if (!start || !end) {
        return '';
      }

      const startDate = moment(start.date);
      const endDate = moment(end.date);

      switch (this.type) {
        case 'month':
          return `Tháng ${startDate.format('MM/YYYY')}`;
        case 'week':
        case '4day':
          return `${startDate.format('DD/MM/YYYY')} - ${endDate.format(
            'DD/MM/YYYY',
          )}`;
        case 'day':
          return startDate.format('DD/MM/YYYY');
        default:
          return '';
      }
    },
  },
  watch: {
    testDrives: {
      handler() {
        this.events = this.testDrives
          .filter((item) => item.startTime && item.endTime)
          .map(this.mapTestDriveToEvent)
          .filter(Boolean);
      },
      deep: true,
    },
    type(newType) {
      this.$nextTick(() => {
        if (this.start && this.end) {
          this.$emit('range-updated', {
            start: this.start.date,
            end: this.end.date,
            type: newType,
          });
        }
      });
    },
  },
  mounted() {
    this.events = this.testDrives;

    this.focus = moment().format('YYYY-MM-DD');
  },
  methods: {
    mapTestDriveToEvent(item) {
      const statusConfig = this.getStatusConfig(item.status);
      const startDate = this.formatDateForCalendar(item.startTime);
      const endDate = this.formatDateForCalendar(item.endTime);

      // Không hiển thị lái thử bị hủy
      if (item.status === TestDriveModel.STATUS.CANCELLED) {
        return null;
      }

      return {
        name: `${item?.productCode || 'N/A'}`,
        start: startDate,
        end: endDate,
        color: this.statusColors[item.status]?.bgColor || '',
        textColor: this.statusColors[item.status]?.textColor || '',
        timed: Boolean(item.startTime && item.endTime),
        productName: item.productName,
        licensePlate: item.licensePlate || 'N/A',
        employeeName: item.employeeName || '-',
        customerName: item?.customer?.fullName || 'Không có thông tin',
        showroomName: item.store?.name || '-',
        statusText: statusConfig.text,
        statusClass: statusConfig.class,

        timeRange: `${this.formatDisplayDateTime(
          item.startTime,
        )} - ${this.formatDisplayDateTime(item.endTime)}`,
        originalItem: item,
      };
    },

    formatDateForCalendar(dateString) {
      if (!dateString) return null;

      try {
        return moment(dateString, YEAR_MONTH_DAY_DATETIME_FORMAT).format(
          DATETIME_DB_FORMAT,
        );
      } catch (error) {
        console.error('Error formatting date for calendar:', error);
        return null;
      }
    },

    formatDisplayDateTime(dateString) {
      if (!dateString) return '-';

      try {
        return moment(dateString, YEAR_MONTH_DAY_DATETIME_FORMAT).format(
          'DD/MM HH:mm',
        );
      } catch (error) {
        console.error('Error formatting display date:', error);
        return '-';
      }
    },

    getStatusConfig(statusId) {
      return TestDriveModel.getStatusById(statusId);
    },

    viewDay({ date }) {
      this.focus = date;
      this.type = 'day';
    },

    getEventColor(event) {
      return event.color;
    },

    setToday() {
      this.focus = moment().format('YYYY-MM-DD');
    },

    prev() {
      this.$refs.calendar?.prev();
    },

    next() {
      this.$refs.calendar?.next();
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        setTimeout(() => (this.selectedOpen = true), 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },

    updateRange({ start, end }) {
      this.start = start;
      this.end = end;

      this.$emit('range-updated', {
        start: start.date,
        end: end.date,
        type: this.type,
      });
    },

    editSelectedEvent() {
      if (this.selectedEvent?.originalItem?.id) {
        this.$router.push({
          name: 'test-drive.edit',
          query: { id: this.selectedEvent.originalItem.id },
        });
      }
    },

    canEdit(item) {
      if (!item) return false;
      return Number(item.status) <= TestDriveModel.STATUS.CANCELLED;
    },
  },
};
</script>

<style lang="scss" scoped>
.test-drive-calendar {
  width: 100%;
  max-width: 100%;

  .v-event {
    cursor: pointer;
    overflow: hidden;
  }

  .calendar-toolbar,
  .calendar-content {
    width: 100%;
    max-width: 100%;
  }

  .status-legend {
    display: flex;
    align-items: center;

    .status-dot {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      margin-right: 6px;
    }

    .status-text {
      font-size: 0.875rem;
    }
  }

  .event-content {
    padding: 2px 2px;
    font-size: 0.85rem;
    line-height: 1.4;
    height: 100%;
    overflow: hidden;

    .status-badge {
      display: inline-block;
      padding: 2px 4px;
      border-radius: 3px;
      font-size: 0.7rem;
      white-space: nowrap;
    }
  }

  // Status classes
  .label-registered {
    background-color: rgba(25, 118, 210, 0.2);
    color: #1565c0;
  }

  .label-confirmed {
    background-color: rgba(76, 175, 80, 0.2);
    color: #2e7d32;
  }

  .label-completed {
    background-color: rgba(158, 158, 158, 0.2);
    color: #67fa6f;
  }

  .label-cancelled {
    background-color: rgba(244, 67, 54, 0.1);
    color: #d32f2f;
  }
}
</style>

<style lang="scss">
/* Today styling - màu xám */
.v-calendar-weekly__day.v-present {
  background-color: rgba(158, 158, 158, 0.1) !important;

  .v-btn {
    background-color: rgba(255, 255, 255, 0.795) !important;
    color: #000000 !important;
    font-weight: 900 !important;
  }
}
</style>
